import { schools, getSchoolColor, computeEnrollmentBySchool, computeAvgDistanceBySchool, getHeatmapColor } from './data';
import type { MapState } from './types';

interface EnrollmentChartProps {
  state: MapState;
}

export function EnrollmentChart({ state }: EnrollmentChartProps) {
  const years = [
    '2026', '2027', '2028', '2029', '2030',
    '2031', '2032', '2033', '2034', '2035', '2036',
  ];

  // Compute enrollment for each year
  const enrollmentByYear = years.map((year) =>
    computeEnrollmentBySchool(state.areaAssignments, year),
  );

  // Find max enrollment across ALL schools for scaling
  const maxEnrollment = Math.max(
    ...enrollmentByYear.flatMap((e) =>
      schools.map((s) => e[s.name] ?? 0),
    ),
    1,
  );

  return (
    <div>
      <div className="text-xs font-semibold text-davis-slate uppercase tracking-wide mb-2">
        Enrollment Projections
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-1 pr-1 font-semibold text-davis-slate whitespace-nowrap">
                School
              </th>
              {years.map((y) => (
                <th
                  key={y}
                  className={`text-right py-1 px-0.5 font-semibold whitespace-nowrap ${
                    y === state.selectedYear
                      ? 'text-davis-navy bg-davis-navy/5 rounded'
                      : 'text-davis-slate'
                  }`}
                >
                  {y.slice(2)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schools.map((school) => {
              const isOpen = state.schoolStatus[school.name] ?? true;
              const color = getSchoolColor(school.name);
              return (
                <tr
                  key={school.name}
                  className={`border-b border-gray-100 ${!isOpen ? 'opacity-40' : ''}`}
                >
                  <td className="py-1 pr-1 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: isOpen ? color : '#999' }}
                      />
                      <span
                        className={`font-medium ${
                          isOpen ? 'text-davis-charcoal' : 'text-gray-400 line-through'
                        }`}
                      >
                        {school.name.replace(' ES', '')}
                      </span>
                    </div>
                  </td>
                  {years.map((year, yi) => {
                    const val = enrollmentByYear[yi][school.name] ?? 0;
                    return (
                      <td
                        key={year}
                        className={`text-right py-1 px-0.5 tabular-nums ${
                          year === state.selectedYear
                            ? 'font-bold text-davis-navy bg-davis-navy/5'
                            : 'text-davis-slate'
                        }`}
                      >
                        {isOpen ? val : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr className="font-bold">
              <td className="py-1 pr-1 text-davis-charcoal">Total</td>
              {years.map((year, yi) => {
                const total = schools.reduce(
                  (sum, s) =>
                    state.schoolStatus[s.name]
                      ? sum + (enrollmentByYear[yi][s.name] ?? 0)
                      : sum,
                  0,
                );
                return (
                  <td
                    key={year}
                    className={`text-right py-1 px-0.5 tabular-nums ${
                      year === state.selectedYear
                        ? 'text-davis-navy bg-davis-navy/5'
                        : 'text-davis-charcoal'
                    }`}
                  >
                    {total}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bar chart for selected year - all schools */}
      <div className="mt-3 space-y-1">
        <div className="text-xs font-semibold text-davis-slate">
          {state.selectedYear} Enrollment
        </div>
        {schools.map((school) => {
          const isOpen = state.schoolStatus[school.name] ?? true;
          const enrollment =
            computeEnrollmentBySchool(state.areaAssignments, state.selectedYear)[
              school.name
            ] ?? 0;
          const pct = isOpen ? (enrollment / maxEnrollment) * 100 : 0;
          const color = getSchoolColor(school.name);

          return (
            <div
              key={school.name}
              className={`flex items-center gap-1.5 ${!isOpen ? 'opacity-40' : ''}`}
            >
              <span className="text-[10px] text-davis-slate w-[70px] truncate">
                {school.name.replace(' ES', '')}
              </span>
              <div className="flex-1 bg-gray-100 rounded-full h-3.5 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-1"
                  style={{
                    width: `${Math.max(pct, isOpen ? 2 : 0)}%`,
                    backgroundColor: isOpen ? color : '#999',
                  }}
                >
                  {isOpen && (
                    <span className="text-[9px] font-bold text-white drop-shadow">
                      {enrollment}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Distance bar chart for selected year */}
      <DistanceChart state={state} />
    </div>
  );
}

function DistanceChart({ state }: { state: MapState }) {
  const avgDistances = computeAvgDistanceBySchool(state.areaAssignments, state.selectedYear);

  // Find max distance among open schools for bar scaling
  const maxDistance = Math.max(
    ...schools
      .filter((s) => state.schoolStatus[s.name])
      .map((s) => avgDistances[s.name] ?? 0),
    0.1,
  );

  return (
    <div className="mt-4 space-y-1">
      <div className="text-xs font-semibold text-davis-slate">
        {state.selectedYear} Avg. Travel Distance
      </div>
      {schools.map((school) => {
        const isOpen = state.schoolStatus[school.name] ?? true;
        const distance = avgDistances[school.name] ?? 0;
        const pct = isOpen ? (distance / maxDistance) * 100 : 0;
        const barColor = isOpen ? getHeatmapColor(distance) : '#999';

        return (
          <div
            key={school.name}
            className={`flex items-center gap-1.5 ${!isOpen ? 'opacity-40' : ''}`}
          >
            <span className="text-[10px] text-davis-slate w-[70px] truncate">
              {school.name.replace(' ES', '')}
            </span>
            <div className="flex-1 bg-gray-100 rounded-full h-3.5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-1"
                style={{
                  width: `${Math.max(pct, isOpen ? 8 : 0)}%`,
                  backgroundColor: barColor,
                }}
              >
                {isOpen && (
                  <span className="text-[9px] font-bold text-white drop-shadow">
                    {distance.toFixed(2)}mi
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Color legend strip */}
      <div className="flex items-center gap-2 mt-1.5 pt-1">
        <span className="text-[9px] text-davis-slate">0mi</span>
        <div
          className="flex-1 h-2 rounded-full"
          style={{
            background: `linear-gradient(to right, ${getHeatmapColor(0)}, ${getHeatmapColor(0.75)}, ${getHeatmapColor(1.5)}, ${getHeatmapColor(2.25)}, ${getHeatmapColor(3)})`,
          }}
        />
        <span className="text-[9px] text-davis-slate">3+mi</span>
      </div>
    </div>
  );
}
